/** biome-ignore-all lint/a11y/noNoninteractiveTabindex: Carousel root needs tabindex for APG keyboard navigation (Arrow/Home/End/PageUp/PageDown). */

import {
	PdfDocumentView,
	type PdfDocumentViewHandle,
} from "@ui/molecules/PdfDocumentView/PdfDocumentView";
import {
	ThumbnailTrack,
	ThumbnailTrackCaption,
	type ThumbnailTrackHandle,
	type ThumbnailTrackItem,
} from "@ui/molecules/ThumbnailTrack/ThumbnailTrack";
import { VStack } from "@ui/Stack";
import { Text } from "@ui/typography/Text";
import { createMemo, createSignal, onCleanup, onMount, Show } from "solid-js";
import type { DocumentPreview } from "../ChatEventTypes.ts";
import { useChatCardToolbarActions } from "../chat-panel/components/ChatCardToolbar";
import {
	DOCUMENT_PREVIEW_PAGE_EVENT,
	type DocumentPreviewPageShownDetail,
	type DocumentPreviewToolbar,
	openDocumentPreviewPanel,
	showDocumentPreviewPage,
} from "./documentPanelEvents";

type Props = {
	document: DocumentPreview;
	/**
	 * The embedded app hides the chat layout's slide-out pane, so there the page
	 * is shown inline instead of being opened in it.
	 */
	appEmbedded?: boolean;
	/** The card's toolbar, travelling with the document into the pane. */
	toolbar?: DocumentPreviewToolbar;
};

/** Portrait pages need a taller strip than landscape slides to read as pages. */
const PAGE_THUMBNAIL_HEIGHT = "11rem";

/** Height of the inline viewer. Its width fills the conversation column. */
const PDF_VIEW_HEIGHT = "28rem";

/**
 * Preview of a hydrated `document` artifact: a page thumbnail carousel.
 * Selecting a page opens it in the chat layout's slide-out pane, which is far
 * wider than the conversation column, the same way selecting a clip opens the
 * video pane. The document's summary goes with it, to be read under the title
 * there, and stays on the card only where there is no pane to open.
 *
 * A Word document has no page viewer, because only the format that was asked
 * for is generated, and a `.docx` has no pages until Word lays it out. The page
 * thumbnails come from a PDF rendered for that purpose and thrown away, so they
 * are all there is to show.
 */
export const DocumentView = (props: Props) => {
	const [selectedIndex, setSelectedIndex] = createSignal(0);
	const toolbarActions = useChatCardToolbarActions();
	let trackHandle: ThumbnailTrackHandle | undefined;
	let viewerHandle: PdfDocumentViewHandle | undefined;

	/**
	 * A thumbnail's own page number, which is authoritative: thumbnails stop at
	 * the first hundred pages, so an index is not a page beyond that.
	 */
	const pageNumber = (index: number) =>
		props.document.pages[index]?.page ?? index + 1;

	/**
	 * Where the page gets shown: the slide-out pane normally, or inline in the
	 * embedded app, which has no pane. Both are undefined for a Word document,
	 * which has no PDF to show.
	 */
	const inlineUrl = () =>
		props.appEmbedded === true ? props.document.pdfUrl : undefined;
	const paneUrl = () =>
		props.appEmbedded === true ? undefined : props.document.pdfUrl;

	const thumbnailLabel = (index: number) => {
		const position = `Page ${pageNumber(index)} of ${props.document.pages.length}`;
		if (paneUrl() !== undefined) {
			return `${position}. Select to open this page in the document preview.`;
		}
		if (inlineUrl() !== undefined) {
			return `${position}. Select to show this page in the viewer above.`;
		}
		return position;
	};

	const pages = createMemo<ThumbnailTrackItem[]>(() =>
		props.document.pages.map((page, index) => ({
			url: page.url,
			width: page.width_px,
			height: page.height_px,
			ariaLabel: thumbnailLabel(index),
		})),
	);

	const selectedPage = createMemo(() => props.document.pages[selectedIndex()]);

	const selectedPageLabel = createMemo(
		() =>
			`${props.document.title}, page ${pageNumber(selectedIndex())} of ${props.document.pages.length}`,
	);

	const fallbackImage = createMemo(() => {
		const page = selectedPage();
		return page !== undefined
			? { url: page.url, alt: selectedPageLabel() }
			: undefined;
	});

	const previewContent = (index: number, url: string) => ({
		url,
		page: pageNumber(index),
		totalPages: props.document.pages.length,
		title: props.document.title,
		summary: props.document.summary,
		toolbar: props.toolbar,
		toolbarActions: { save: toolbarActions.save, rate: toolbarActions.rate },
	});

	/**
	 * Whether a thumbnail has a pane to open, which is where the summary is
	 * read. A Word document has no PDF for one to show, and the embedded app has
	 * no pane at all.
	 */
	const opensInPane = () =>
		paneUrl() !== undefined && props.document.pages.length > 0;

	/**
	 * Moving along the strip, by its arrows or the keyboard. A viewer already
	 * showing this document turns to the page; nothing here opens one.
	 */
	const selectPage = (index: number) => {
		setSelectedIndex(index);
		const url = paneUrl();
		if (url !== undefined) {
			showDocumentPreviewPage(previewContent(index, url));
		}
	};

	/**
	 * Choosing a page outright. Both viewers show the whole document, so a
	 * thumbnail that is already the selected one still has somewhere to take a
	 * reader who has scrolled away from it.
	 */
	const openPage = (index: number) => {
		const url = paneUrl();
		if (url === undefined) {
			viewerHandle?.goToPage(pageNumber(index));
			return;
		}
		openDocumentPreviewPanel(previewContent(index, url));
	};

	/*
	 * Reading in the pane moves the strip too, so the thumbnail marked as the
	 * one being read stays the page the reader is on. Only this document's pane
	 * is heard, and only pages with a thumbnail to mark: the strip stops at the
	 * first hundred.
	 */
	onMount(() => {
		const handlePageShown = (event: Event) => {
			const { url, page } = (
				event as CustomEvent<DocumentPreviewPageShownDetail>
			).detail;
			if (url !== paneUrl()) {
				return;
			}
			const index = props.document.pages.findIndex(
				(thumbnail) => thumbnail.page === page,
			);
			if (index === -1 || index === selectedIndex()) {
				return;
			}
			setSelectedIndex(index);
			trackHandle?.scrollToIndex(index);
		};
		document.addEventListener(DOCUMENT_PREVIEW_PAGE_EVENT, handlePageShown);
		onCleanup(() =>
			document.removeEventListener(
				DOCUMENT_PREVIEW_PAGE_EVENT,
				handlePageShown,
			),
		);
	});

	return (
		<VStack spacing="medium">
			{/* The summary is read in the pane, under the document's title, and is
			    shown here only where no page has a pane to open. */}
			<Show when={opensInPane() ? undefined : props.document.summary}>
				{(summary) => (
					<Text size="standard" color="fg2">
						{summary()}
					</Text>
				)}
			</Show>
			<Show when={pages().length > 0}>
				<section
					aria-roledescription="carousel"
					aria-label="Document pages"
					tabIndex={0}
					onKeyDown={(e) => trackHandle?.onKeyDown(e)}
				>
					<VStack spacing="large">
						<Show when={inlineUrl()}>
							{(pdfUrl) => (
								<PdfDocumentView
									url={pdfUrl()}
									page={pageNumber(selectedIndex())}
									height={PDF_VIEW_HEIGHT}
									fallback={fallbackImage()}
									label={`${props.document.title}, ${props.document.pages.length} pages`}
									handle={(handle) => {
										viewerHandle = handle;
									}}
								/>
							)}
						</Show>
						<ThumbnailTrackCaption
							title={props.document.title}
							selectedIndex={selectedIndex()}
							total={pages().length}
						/>
						<ThumbnailTrack
							items={pages()}
							selectedIndex={selectedIndex()}
							onSelect={selectPage}
							onActivate={openPage}
							itemLabel="Page"
							thumbnailHeight={PAGE_THUMBNAIL_HEIGHT}
							handle={(handle) => {
								trackHandle = handle;
							}}
						/>
					</VStack>
				</section>
			</Show>
		</VStack>
	);
};
